import React, { useState, useEffect, useMemo, useRef } from "react";
import { canteensApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./Canteens.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const BLANK_FORM = {
  _id: null,
  Title: "",
  Subtitle: "",
  Description: "",
  Available_Food_Items: [],
};

const Canteens = () => {
  const [canteens, setCanteens]             = useState([]);
  const [loading, setLoading]               = useState(false);
  const [modalType, setModalType]           = useState("");
  const [formData, setFormData]             = useState(BLANK_FORM);
  const [confirmId, setConfirmId]           = useState(null);
  const [searchText, setSearchText]         = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos]       = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  useEffect(() => { fetchCanteens(); }, []);

  const fetchCanteens = async () => {
    try {
      setLoading(true);
      const response = await apiClient(canteensApi);
      if (response.ok) {
        const result = await response.json();
        setCanteens(result.data || []);
      } else {
        console.error("API Response not OK:", response.status);
      }
    } catch (error) {
      console.error("Error fetching canteens:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const onClose = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => canteens.filter(c => {
    const q = searchText.toLowerCase();
    return (c.Title || "").toLowerCase().includes(q) || (c.Subtitle || "").toLowerCase().includes(q);
  }), [canteens, searchText]);

  const stats = useMemo(() => ({
    total: canteens.length,
    foodItems: canteens.reduce((acc, curr) => acc + (curr.Available_Food_Items?.length || 0), 0),
    topLocation: canteens.length > 0 ? canteens[0].Title : "None"
  }), [canteens]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 180 });
    setDropdownOpenId(id);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? canteensApi : `${canteensApi}/${formData._id}`;
      const response = await apiClient(url, {
        method,
        body: JSON.stringify(formData),
      });
      if (response.ok) { fetchCanteens(); setModalType(""); }
    } catch (error) {
      console.error("Error saving canteen:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await apiClient(`${canteensApi}/${id}`, { method: "DELETE" });
      if (response.ok) { fetchCanteens(); setConfirmId(null); }
    } catch (error) {
      console.error("Error deleting canteen:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFoodChange = (idx, val) => {
    const next = [...formData.Available_Food_Items];
    next[idx] = val;
    setFormData({ ...formData, Available_Food_Items: next });
  };

  const addFoodRow = () => setFormData({ ...formData, Available_Food_Items: [...formData.Available_Food_Items, ""] });
  const removeFoodRow = (idx) => setFormData({ ...formData, Available_Food_Items: formData.Available_Food_Items.filter((_, i) => i !== idx) });

  return (
    <div className="can-page">
      {/* ── Header ── */}
      <div className="can-header">
        <div className="can-header-left">
          <div className="sh-breadcrumb"> {/* Reusing sh-breadcrumb style for consistency */}
            <span>Facilities</span>
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
            <span className="sh-bc-active">Canteens</span>
          </div>
          <h1>Canteens & Cafeterias</h1>
          <p>Manage cafeteria locations, operational details, and available food menus for students and faculty members.</p>
        </div>
        <button type="button" className="can-add-btn" onClick={() => { setFormData(BLANK_FORM); setModalType("add"); }}>
          + Add Cafeteria
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="can-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <div className="can-card" style={{ padding: 14, flexDirection: 'row', alignItems: 'center' }}>
          <div className="can-icon" style={{ width: 40, height: 40, fontSize: 18 }}>🍽️</div>
          <div className="can-meta"><h3>{stats.total}</h3><p>Total Cafeterias</p></div>
        </div>
        <div className="can-card" style={{ padding: 14, flexDirection: 'row', alignItems: 'center' }}>
          <div className="can-icon" style={{ width: 40, height: 40, fontSize: 18, background: 'linear-gradient(135deg, #f43f5e, #fb7185)' }}>🍕</div>
          <div className="can-meta"><h3>{stats.foodItems}</h3><p>Total Menu Items</p></div>
        </div>
        <div className="can-card" style={{ padding: 14, flexDirection: 'row', alignItems: 'center' }}>
          <div className="can-icon" style={{ width: 40, height: 40, fontSize: 18, background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' }}>📍</div>
          <div className="can-meta"><h3>{stats.topLocation}</h3><p>Featured Venue</p></div>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="can-toolbar">
        <input type="text" placeholder="Search by name, location or food item..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
      </div>

      {loading && <div className="loading-overlay" style={{ textAlign:'center', padding:40 }}>Loading cafeteria records...</div>}

      {/* ── Grid ── */}
      <div className="can-grid">
        {filtered.map((item) => (
          <div key={item._id} className="can-card" onClick={() => { setFormData(item); setModalType("edit"); }}>
            <div className="can-card-head">
              <div className="can-icon">🍲</div>
              <div className="can-meta">
                <h3>{item.Title}</h3>
                <p>{item.Subtitle}</p>
              </div>
              <button type="button" className="can-dots" onClick={(e) => handleDotsClick(e, item._id)}>⋮</button>
            </div>
            <p className="can-description">{item.Description}</p>
            <div className="can-foods">
              {(item.Available_Food_Items || []).slice(0, 4).map((f, i) => (
                <span key={i} className="can-food-pill">{f}</span>
              ))}
              {(item.Available_Food_Items?.length || 0) > 4 && (
                <span className="can-food-more">+{item.Available_Food_Items.length - 4} more</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Empty State ── */}
      {!loading && filtered.length === 0 && (
        <div className="can-empty">
          <h3>No cafeterias found</h3>
          <p>{searchText ? "No records match your search criteria." : "Click '+ Add Cafeteria' to create your first record."}</p>
        </div>
      )}

      {/* ── Dropdown ── */}
      {dropdownOpenId && (
        <div ref={dropdownRef} className="can-dropdown" style={{ top: dropdownPos.top, left: dropdownPos.left }}>
          <button type="button" onClick={() => { setFormData(canteens.find(c => c._id === dropdownOpenId)); setModalType("edit"); setDropdownOpenId(null); }}>Edit Details</button>
          <button type="button" className="danger" onClick={() => { setConfirmId(dropdownOpenId); setDropdownOpenId(null); }}>Delete Record</button>
        </div>
      )}

      {/* ── Modal ── */}
      {(modalType === "add" || modalType === "edit") && (
        <div className="can-overlay" onClick={() => setModalType("")}>
          <div className="can-modal" onClick={(e) => e.stopPropagation()}>
            <div className="can-modal-head">
              <div>
                <h2>{modalType === "add" ? "Add Cafeteria" : "Edit Cafeteria"}</h2>
                <p>Manage location details and cafeteria food menus.</p>
              </div>
              <button type="button" className="can-close" onClick={() => setModalType("")}>×</button>
            </div>
            <form className="can-form" onSubmit={handleSave}>
              <div className="can-field full">
                <label>Cafeteria Name</label>
                <input value={formData.Title || ""} onChange={(e) => setFormData({ ...formData, Title: e.target.value })} placeholder="e.g. Main Campus Canteen" required />
              </div>
              <div className="can-field full">
                <label>Subtitle / Location</label>
                <input value={formData.Subtitle || ""} onChange={(e) => setFormData({ ...formData, Subtitle: e.target.value })} placeholder="e.g. Near Science Block, Ground Floor" />
              </div>
              <div className="can-field full">
                <label>Description</label>
                <textarea value={formData.Description || ""} onChange={(e) => setFormData({ ...formData, Description: e.target.value })} placeholder="Describe operational hours, seating capacity, and hygiene standards..." />
              </div>

              <div className="can-field full">
                <div className="can-food-header">
                  <label>Food Menu Items</label>
                  <button type="button" className="can-mini-btn" onClick={addFoodRow}>+ Add Item</button>
                </div>
                <div className="can-food-list">
                  {formData.Available_Food_Items.map((f, i) => (
                    <div key={i} className="can-food-row">
                      <input value={f} onChange={(e) => handleFoodChange(i, e.target.value)} placeholder="e.g. Veg Thali" />
                      <button type="button" className="can-remove-btn" onClick={() => removeFoodRow(i)}>×</button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="can-actions">
                <button type="button" className="btn-light" onClick={() => setModalType("")}>Cancel</button>
                <button type="submit" className="btn-primary">{modalType === "add" ? "Create Record" : "Save Changes"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Confirm ── */}
      {confirmId && (
        <div className="can-overlay" onClick={() => setConfirmId(null)}>
          <div className="can-confirm" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to permanently delete this cafeteria record? This action cannot be undone.</p>
            <div className="can-actions">
              <button type="button" className="btn-light" onClick={() => setConfirmId(null)}>Cancel</button>
              <button type="button" className="btn-danger" onClick={() => handleDelete(confirmId)}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Canteens;